import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FiMenu, FiX, FiHelpCircle, FiGlobe } from 'react-icons/fi';
import { useTranslation } from 'react-i18next';
import LanguageSelector from './LanguageSelector';
import SupportComingSoon from './SupportComingSoon';
import { getContent } from '../../services/api';

function Navigation({ onCTAClick }) {
  const [isScrolled, setIsScrolled] = useState(false);
  const [scrollProgress, setScrollProgress] = useState(0);
  const [isOverDarkSection, setIsOverDarkSection] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [atlasLink, setAtlasLink] = useState('');
  const [showSupportTooltip, setShowSupportTooltip] = useState(false);
  const [showAtlasTooltip, setShowAtlasTooltip] = useState(false);
  const [showSupportModal, setShowSupportModal] = useState(false);
  const { t } = useTranslation();
  
  // Ref for mobile language selector
  const mobileLangSelectorRef = useRef(null);

  useEffect(() => {
    const handleScroll = () => {
      const scrollY = window.scrollY;
      // Calculate scroll progress (0 to 1) over first 400px of scroll for extended glassmorphism
      const progress = Math.min(scrollY / 400, 1);
      setScrollProgress(progress);
      setIsScrolled(scrollY > 50);
      
      // Check if header is over the dark MIL-Grade section
      const milGradeSection = document.getElementById('mil-grade');
      if (milGradeSection) {
        const rect = milGradeSection.getBoundingClientRect();
        const headerHeight = 80; // Header height in pixels
        // Header is over dark section when section top is above header bottom and section bottom is below header top
        const isOverDark = rect.top < headerHeight && rect.bottom > 0;
        setIsOverDarkSection(isOverDark);
      }
    };
    window.addEventListener('scroll', handleScroll);
    // Initial check
    handleScroll();
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Dynamic styles based on scroll progress - Persistent Colorless Glassmorphism Effect
  // Phase 1 (0-40px): Fully transparent - no effect
  // Phase 2 (40-80px): Transition from transparent to colorless medium frost
  // Phase 3 (80px+): Persistent colorless frost - pure blur, no white tint
  
  const getHeaderStyles = () => {
    const scrollY = window.scrollY || 0;
    
    if (scrollY <= 40) {
      // Phase 1: Fully transparent - clean hero view
      return {
        backgroundColor: 'transparent',
        backdropFilter: 'blur(0px)',
        WebkitBackdropFilter: 'blur(0px)',
        boxShadow: 'none',
      };
    } else if (scrollY <= 80) {
      // Phase 2: Smooth transition to colorless frost (over ~40px scroll distance)
      const transitionProgress = (scrollY - 40) / 40; // 0 to 1 over 40px
      const easeProgress = transitionProgress * transitionProgress * (3 - 2 * transitionProgress); // Smooth ease
      return {
        backgroundColor: 'transparent', // No color tint
        backdropFilter: `blur(${easeProgress * 10}px)`, // 0 to 10px blur for medium frost
        WebkitBackdropFilter: `blur(${easeProgress * 10}px)`,
        boxShadow: 'none',
      };
    } else {
      // Phase 3: Persistent colorless frost - "clear frosted glass panel"
      // Content slides beneath, blurred but retains original colors
      return {
        backgroundColor: 'transparent', // Completely colorless
        backdropFilter: 'blur(10px)', // Medium blur for frosted effect
        WebkitBackdropFilter: 'blur(10px)',
        boxShadow: 'none',
      };
    }
  };

  const headerBgStyle = getHeaderStyles();

  // Adaptive colors based on whether header is over dark section
  // When over dark section (MIL-Grade), use white; otherwise use navy
  const adaptiveTextColor = isOverDarkSection ? '#FFFFFF' : '#101957';
  const adaptiveHoverBg = isOverDarkSection 
    ? 'rgba(255,255,255,0.15)' 
    : (scrollProgress > 0.4 ? 'rgba(0,0,0,0.05)' : 'rgba(16,25,87,0.08)');

  useEffect(() => {
    // Load Atlas link from backend
    const loadAtlasLink = async () => {
      try {
        const response = await getContent();
        if (response.data.atlas_link) {
          setAtlasLink(response.data.atlas_link);
        }
      } catch (error) {
        console.error('Failed to load atlas link:', error);
      }
    };
    loadAtlasLink();
  }, []);

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      // Account for fixed header height (80px) + some padding (20px)
      const headerOffset = 100;
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - headerOffset;
      
      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
      setIsMobileMenuOpen(false);
    }
  };

  const handleSupportClick = () => {
    // Close mobile menu first if open
    setIsMobileMenuOpen(false);
    setShowSupportModal(true);
  };

  const handleServiceRequestClick = () => {
    // Close mobile menu first if open
    setIsMobileMenuOpen(false);
    // Open inquiry modal with "Service Request" pre-selected
    if (onCTAClick) {
      onCTAClick('Service Request');
    }
  };

  return (
    <motion.nav
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      className="fixed top-0 left-0 right-0 transition-all duration-300"
      style={{ 
        zIndex: 100,
        ...headerBgStyle
      }}
    >
      {/* Desktop Navigation - Three Zone Layout */}
      <div className="hidden lg:grid lg:grid-cols-3 items-center h-20 px-3">
        {/* LEFT ZONE - Logo (flush to left) - INCREASED SIZE */}
        <div className="flex justify-start">
          <div 
            className="text-4xl font-bold tracking-wider cursor-pointer transition-colors duration-300"
            style={{
              fontFamily: "'Racing Sans One', cursive",
              color: adaptiveTextColor
            }}
            onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
          >
            HADEN
          </div>
        </div>

        {/* CENTER ZONE - Navigation Links with Rounded Background Hover */}
        <div className="flex items-center justify-center space-x-6">
          <button
            onClick={() => scrollToSection('product-classes')}
            className="font-medium transition-all duration-300 px-3 py-2 rounded-lg text-sm"
            style={{ 
              color: adaptiveTextColor,
            }}
            onMouseEnter={(e) => e.target.style.backgroundColor = adaptiveHoverBg}
            onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
          >
            {t('nav.solutions', { defaultValue: 'Solutions' })}
          </button>
          <button
            onClick={() => scrollToSection('programs')}
            className="font-medium transition-all duration-300 px-3 py-2 rounded-lg text-sm"
            style={{ 
              color: adaptiveTextColor,
            }}
            onMouseEnter={(e) => e.target.style.backgroundColor = adaptiveHoverBg}
            onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
          >
            {t('nav.approach', { defaultValue: 'Approach' })}
          </button>
          <button
            onClick={() => scrollToSection('enablers')}
            className="font-medium transition-all duration-300 px-3 py-2 rounded-lg text-sm"
            style={{ 
              color: adaptiveTextColor,
            }}
            onMouseEnter={(e) => e.target.style.backgroundColor = adaptiveHoverBg}
            onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
          >
            {t('nav.expertise', { defaultValue: 'Expertise' })}
          </button>
          <button
            onClick={() => scrollToSection('proof-points')}
            className="font-medium transition-all duration-300 px-3 py-2 rounded-lg text-sm"
            style={{ 
              color: adaptiveTextColor,
            }}
            onMouseEnter={(e) => e.target.style.backgroundColor = adaptiveHoverBg}
            onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
          >
            {t('nav.aboutUs', { defaultValue: 'About Us' })}
          </button>
          <button
            onClick={() => scrollToSection('contact')}
            className="font-medium transition-all duration-300 px-3 py-2 rounded-lg text-sm"
            style={{ 
              color: adaptiveTextColor,
            }}
            onMouseEnter={(e) => e.target.style.backgroundColor = adaptiveHoverBg}
            onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
          >
            {t('nav.contact', { defaultValue: 'Contact' })}
          </button>
        </div>

        {/* RIGHT ZONE - Actions (flush to right) */}
        <div className="flex items-center justify-end space-x-3">
          {/* Support Icon with Properly Centered Tooltip */}
          <div className="relative inline-block">
            <button
              onClick={handleSupportClick}
              onMouseEnter={() => setShowSupportTooltip(true)}
              onMouseLeave={() => setShowSupportTooltip(false)}
              className="p-2 rounded-full transition-all duration-300"
              style={{ 
                backgroundColor: 'transparent',
              }}
              onMouseOver={(e) => e.currentTarget.style.backgroundColor = adaptiveHoverBg}
              onMouseOut={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
              data-testid="support-btn"
            >
              <FiHelpCircle className="text-xl transition-colors duration-300" style={{ color: adaptiveTextColor }} />
            </button>

            {/* Support Tooltip - NO ARROW */}
            <AnimatePresence>
              {showSupportTooltip && (
                <motion.div
                  initial={{ opacity: 0, y: -5 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -5 }}
                  transition={{ duration: 0.2 }}
                  className="absolute pointer-events-none z-50"
                  style={{
                    top: 'calc(100% + 8px)',
                    left: '50%',
                    transform: 'translateX(-50%)',
                  }}
                >
                  <div
                    className="px-3 py-1.5 text-xs font-medium whitespace-nowrap rounded-lg"
                    style={{
                      backgroundColor: 'rgba(15, 23, 42, 0.9)',
                      color: 'white'
                    }}
                  >
                    {t('nav.support', { defaultValue: 'Support' })}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          {/* Language Selector Icon with Centered Tooltip */}
          <LanguageSelector adaptiveTextColor={adaptiveTextColor} adaptiveHoverBg={adaptiveHoverBg} />

          {/* Atlas Login Button with Tooltip */}
          <div className="relative inline-block">
            <a
              href={atlasLink}
              target="_blank"
              rel="noopener noreferrer"
              className="px-4 py-2 rounded font-medium transition-all duration-300 text-sm inline-block"
              style={{
                backgroundColor: '#101957',
                color: 'white'
              }}
              data-testid="atlas-login-btn"
              onMouseEnter={() => setShowAtlasTooltip(true)}
              onMouseLeave={() => setShowAtlasTooltip(false)}
              onClick={(e) => {
                e.preventDefault();
                setShowAtlasTooltip(true);
                setTimeout(() => setShowAtlasTooltip(false), 2000);
              }}
            >
              <span>Login to </span>
              <span 
                style={{ 
                  fontFamily: "'Racing Sans One', cursive",
                  fontSize: '1.15em'
                }}
              >
                HADEN
              </span>
              <span> </span>
              <span 
                style={{ 
                  fontFamily: "'Abril Fatface', serif",
                  color: '#C8B59A'
                }}
              >
                ATLAS
              </span>
            </a>

            {/* Atlas Tooltip */}
            <AnimatePresence>
              {showAtlasTooltip && (
                <motion.div
                  initial={{ opacity: 0, y: -5 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -5 }}
                  transition={{ duration: 0.2 }}
                  className="absolute pointer-events-none z-50"
                  style={{
                    top: 'calc(100% + 8px)',
                    right: '0',
                  }}
                >
                  <div
                    className="px-3 py-1.5 text-xs font-medium whitespace-nowrap rounded-lg"
                    style={{
                      backgroundColor: 'rgba(15, 23, 42, 0.9)',
                      color: 'white'
                    }}
                  >
                    {t('nav.atlasComingSoon', { defaultValue: 'Coming Soon — Portal activation in progress' })}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>
      </div>

      {/* Mobile Navigation */}
      <div className="lg:hidden">
        <div 
          className="flex items-center justify-between h-20 px-4 transition-all duration-300"
        >
          {/* Mobile Logo */}
          <div 
            className="text-3xl font-bold tracking-wider cursor-pointer transition-colors duration-300"
            style={{
              fontFamily: "'Racing Sans One', cursive",
              color: adaptiveTextColor
            }}
            onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
          >
            HADEN
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="p-2 rounded-lg transition-all duration-300"
            style={{ 
              color: adaptiveTextColor,
              backgroundColor: 'transparent'
            }}
            onMouseOver={(e) => e.currentTarget.style.backgroundColor = adaptiveHoverBg}
            onMouseOut={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
            aria-label="Toggle menu"
          >
            <FiMenu className="text-2xl" />
          </button>
        </div>

        {/* Mobile Menu Overlay - Using Portal-like positioning */}
        <AnimatePresence>
          {isMobileMenuOpen && (
            <>
              {/* Dark Backdrop - Fixed to viewport */}
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.2 }}
                className="fixed inset-0 bg-black/50"
                style={{ zIndex: 9998 }}
                onClick={() => setIsMobileMenuOpen(false)}
              />

              {/* Slide-in Menu Panel - Fixed to viewport */}
              <motion.div
                initial={{ x: '100%' }}
                animate={{ x: 0 }}
                exit={{ x: '100%' }}
                transition={{ type: 'tween', duration: 0.3, ease: 'easeOut' }}
                className="fixed top-0 right-0 h-screen w-[85%] max-w-sm bg-white shadow-2xl overflow-y-auto"
                style={{ zIndex: 9999 }}
              >
                {/* Menu Header */}
                <div className="flex items-center justify-between p-5 border-b border-gray-100">
                  <div 
                    className="text-2xl font-bold tracking-wider"
                    style={{
                      fontFamily: "'Racing Sans One', cursive",
                      color: '#101957'
                    }}
                  >
                    HADEN
                  </div>
                  <button
                    onClick={() => setIsMobileMenuOpen(false)}
                    className="p-2 rounded-full hover:bg-gray-100 transition-colors"
                    style={{ color: '#101957' }}
                    aria-label="Close menu"
                  >
                    <FiX className="text-2xl" />
                  </button>
                </div>

                {/* Menu Content */}
                <div className="p-5">
                  {/* Navigation Links */}
                  <nav className="space-y-1">
                    <button
                      onClick={() => scrollToSection('product-classes')}
                      className="flex items-center justify-between w-full text-left py-4 px-3 rounded-lg hover:bg-gray-50 transition-colors group"
                    >
                      <span className="font-medium text-lg" style={{ color: '#101957' }}>
                        {t('nav.solutions', { defaultValue: 'Solutions' })}
                      </span>
                      <span className="text-gray-400 group-hover:text-gray-600 transition-colors">→</span>
                    </button>
                    
                    <div className="border-b border-gray-100 mx-3"></div>
                    
                    <button
                      onClick={() => scrollToSection('programs')}
                      className="flex items-center justify-between w-full text-left py-4 px-3 rounded-lg hover:bg-gray-50 transition-colors group"
                    >
                      <span className="font-medium text-lg" style={{ color: '#101957' }}>
                        {t('nav.approach', { defaultValue: 'Approach' })}
                      </span>
                      <span className="text-gray-400 group-hover:text-gray-600 transition-colors">→</span>
                    </button>
                    
                    <div className="border-b border-gray-100 mx-3"></div>
                    
                    <button
                      onClick={() => scrollToSection('enablers')}
                      className="flex items-center justify-between w-full text-left py-4 px-3 rounded-lg hover:bg-gray-50 transition-colors group"
                    >
                      <span className="font-medium text-lg" style={{ color: '#101957' }}>
                        {t('nav.expertise', { defaultValue: 'Expertise' })}
                      </span>
                      <span className="text-gray-400 group-hover:text-gray-600 transition-colors">→</span>
                    </button>
                    
                    <div className="border-b border-gray-100 mx-3"></div>
                    
                    <button
                      onClick={() => scrollToSection('proof-points')}
                      className="flex items-center justify-between w-full text-left py-4 px-3 rounded-lg hover:bg-gray-50 transition-colors group"
                    >
                      <span className="font-medium text-lg" style={{ color: '#101957' }}>
                        {t('nav.aboutUs', { defaultValue: 'About Us' })}
                      </span>
                      <span className="text-gray-400 group-hover:text-gray-600 transition-colors">→</span>
                    </button>
                    
                    <div className="border-b border-gray-100 mx-3"></div>
                    
                    <button
                      onClick={() => scrollToSection('contact')}
                      className="flex items-center justify-between w-full text-left py-4 px-3 rounded-lg hover:bg-gray-50 transition-colors group"
                    >
                      <span className="font-medium text-lg" style={{ color: '#101957' }}>
                        {t('nav.contact', { defaultValue: 'Contact' })}
                      </span>
                      <span className="text-gray-400 group-hover:text-gray-600 transition-colors">→</span>
                    </button>
                  </nav>

                  {/* Utility Links */}
                  <div className="mt-6 pt-6 border-t border-gray-200 space-y-1">
                    <button
                      onClick={handleSupportClick}
                      className="flex items-center w-full text-left py-3 px-3 rounded-lg hover:bg-gray-50 transition-colors"
                    >
                      <FiHelpCircle className="text-xl mr-3" style={{ color: '#101957' }} />
                      <span className="font-medium" style={{ color: '#101957' }}>{t('nav.support', { defaultValue: 'Support' })}</span>
                    </button>
                    
                    {/* Language Row - Fully clickable like Support row */}
                    <div className="relative">
                      <button
                        onClick={() => mobileLangSelectorRef.current?.toggle()}
                        className="flex items-center w-full text-left py-3 px-3 rounded-lg hover:bg-gray-50 transition-colors"
                      >
                        <FiGlobe className="text-xl mr-3" style={{ color: '#101957' }} />
                        <span className="font-medium" style={{ color: '#101957' }}>{t('nav.language', { defaultValue: 'Language' })}</span>
                      </button>
                      {/* Language Selector Dropdown - Centered */}
                      <LanguageSelector ref={mobileLangSelectorRef} isMobile={true} />
                    </div>
                  </div>

                  {/* Atlas Login CTA */}
                  <div className="mt-8">
                    <button
                      onClick={() => {
                        alert(t('nav.atlasComingSoon', { defaultValue: 'Coming Soon — Portal activation in progress' }));
                      }}
                      className="block w-full px-6 py-4 rounded-xl font-medium text-center transition-all duration-300 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
                      style={{
                        backgroundColor: '#101957',
                        color: 'white'
                      }}
                    >
                      <span>{t('nav.loginTo', { defaultValue: 'Login to' })} </span>
                      <span style={{ fontFamily: "'Racing Sans One', cursive", fontSize: '1.15em' }}>HADEN</span>
                      <span> </span>
                      <span style={{ fontFamily: "'Abril Fatface', serif", color: '#C8B59A' }}>ATLAS</span>
                    </button>
                    <p className="text-xs text-center mt-2" style={{ color: '#64748b' }}>
                      {t('nav.atlasComingSoon', { defaultValue: 'Coming Soon — Portal activation in progress' })}
                    </p>
                  </div>

                  {/* Footer Text */}
                  <div className="mt-8 pt-6 border-t border-gray-100 text-center">
                    <p className="text-sm text-gray-500">
                      {t('footer.company', { defaultValue: 'Precision Engineering for Aerospace & Defense' })}
                    </p>
                  </div>
                </div>
              </motion.div>
            </>
          )}
        </AnimatePresence>
      </div>

      {/* Support Coming Soon Modal */}
      <SupportComingSoon 
        isOpen={showSupportModal}
        onClose={() => setShowSupportModal(false)}
        onServiceRequestClick={handleServiceRequestClick}
      />
    </motion.nav>
  );
}

export default Navigation;
